import React, { useEffect, useMemo, useState } from 'react';
import ArticleIcon from '@mui/icons-material/Article';
import { useTranslation } from 'react-i18next';
import BasicCli from '../../main/components/basicCli/BasicCli';
import GeneralTable from '../../main/components/grid/GeneralTable';
import EditModal from '../../main/components/modal/edit/EditModal';
import axiosInstance from '../../main/initializers/axios';
import { ApiConstants } from '../../main/constants/ApiConstants';
import ILogger from '../../main/interfaces/ILogger';
import IGridItems from '../../main/interfaces/IGridItems';
import IPagination from '../../main/interfaces/IPagination';
import eFormType from '../../main/assets/enums/eFormType';
import './LoggerGrid.css';

const LOG_GRID_ENTITY = 17;

const getLevelLabel = (level?: number | string) => {
  const value = typeof level === 'string' ? Number(level) : level;
  switch (value) {
    case 1:
      return 'Info';
    case 2:
      return 'Warning';
    default:
     return 'Error';
  }
};

const LoggerGrid = () => {
  const { i18n } = useTranslation();
  const [logs, setLogs] = useState<ILogger[]>([]);
  const [selectedLog, setSelectedLog] = useState<ILogger | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [gridItems, setGridItems] = useState<IGridItems[]>([]);
  const [pagination, setPagination] = useState<IPagination>({
    count: 1,
    page: 1,
    itemPerPage: 50,
    itemsPerPage: [10, 20, 50, 100],
  });

  const getTable = (pag?: IPagination, gridOverride?: IGridItems[]) => {
    const effective = pag ?? pagination;
    const effectiveGrid = gridOverride ?? gridItems ?? [];

    axiosInstance.post(ApiConstants.logger.listCt, { gridItems: effectiveGrid, pagination: effective }).then((res) => {
      const items = res.data?.items ?? [];
      const p = res.data?.pagination ?? {};
      const cols = res.data?.gridItems ?? [];

      setLogs(items);
      setGridItems(cols);
      setPagination((prev) => ({
        ...prev,
        count: p.count ?? prev.count,
        page: p.page ?? prev.page,
        itemPerPage: p.itemPerPage ?? prev.itemPerPage,
        itemsPerPage: p.itemsPerPage ?? prev.itemsPerPage,
      }));
    });
  };

  useEffect(() => {
    getTable();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredLogs = useMemo(() => {
    return logs.map((item) => ({
      ...item,
      level: getLevelLabel(item.level),
    }));
  }, [logs]);

  const openDetails = (row: ILogger) => {
    axiosInstance.get(ApiConstants.logger.byId(row.id)).then((res) => {
      setSelectedLog(res.data ?? row);
      setIsDetailOpen(true);
    });
  };

  return (
    <BasicCli>
      <div className="lgWrap bcPageContent">
        <div className="lgContainer">
          <div className="bcPageHeader">
            <h1 className="bcPageTitle">{i18n.t('logger.title', { defaultValue: 'Logs' })}</h1>
          </div>

          <div className=" lgTable">
            <GeneralTable
              data={filteredLogs}
              columns={gridItems}
              gridEntity={LOG_GRID_ENTITY}
              title=""
              hasRp={false}
              selectedItem={openDetails}
              pagination={pagination}
              selectedItemPagination={(val) => {
                setPagination(val);
                getTable(val);
              }}
              onFilterClose={(nextGridItems) => {
                setGridItems(nextGridItems);
                getTable(undefined, nextGridItems);
              }}
            />
          </div>
        </div>
      </div>

      {isDetailOpen && (
        <EditModal
          title={i18n.t('logger.detailTitle', { defaultValue: 'Log detail' })}
          width="80%"
          height="50vh"
          closeModal={() => setIsDetailOpen(false)}
          setIsModalOpen={setIsDetailOpen}
          onSubmitData={() => setIsDetailOpen(false)}
          modalType={eFormType.Edit}
          hideApproveButton
        >
          <div className="lgDetailMeta">
            <div>
              <strong>{i18n.t('entity.Id')}:</strong> {selectedLog?.id}
            </div>
            <div>
              <strong>{i18n.t('entity.DateTime', { defaultValue: 'Date/Time' })}:</strong> {selectedLog?.dateTime ?? '-'}
            </div>
            <div>
              <strong>{i18n.t('entity.Level', { defaultValue: 'Level' })}:</strong> {getLevelLabel(selectedLog?.level)}
            </div>
            <div>
              <strong>{i18n.t('entity.Class', { defaultValue: 'Source' })}:</strong> {selectedLog?.class ?? '-'}
            </div>
          </div>

          <div className="lgDetailSection">
            <div className="lgDetailLabel">
              <ArticleIcon fontSize="small" />
              {i18n.t('entity.Message', { defaultValue: 'Message' })}
            </div>
            <pre className="lgDetailPre">{selectedLog?.message || '-'}</pre>
          </div>

          <div className="lgDetailSection">
            <div className="lgDetailLabel">
              <ArticleIcon fontSize="small" />
              {i18n.t('logger.stack', { defaultValue: 'Stack trace' })}
            </div>
            <pre className="lgDetailPre">{selectedLog?.stack || '-'}</pre>
          </div>
        </EditModal>
      )}
    </BasicCli>
  );
};

export default LoggerGrid;
